import React, { useState } from 'react';
import {
  LoadingIndicator,
  ModalVideoListVideo,
  TableContextProvider,
  Search,
  TablePaginationNew,
  ModalDelete,
} from 'lib/components';
import { Button } from 'react-covideo-common';
import { Gap } from '../../styles/layout';
import { ParagraphSmallBold } from '../../styles/typography';
import { theme } from 'lib/style';
import { VideoItemThumbnail } from 'app/pages/library/videos/components';
import { debounce } from 'lodash';
import { MultiselectDropdown } from 'lib/components/dropdown/MultiselectDropdown';
import { PaginationConstants } from 'lib/const/PaginationConstants';
import { SortButtons } from 'app/pages/admin/components/SortButtons';
import {
  OrganizationVideo,
  OrganizationVideoSortKey,
} from 'lib/api/organizationVideo/useOrganizationVideoQuery';
import { OrganizationVideoFilter } from 'lib/api/organizationVideo/useOrganizationVideoFiltersQuery';
import {
  MdAdd,
  MdContentCopy,
  MdDeleteForever,
  MdEdit,
  MdShortText,
} from 'react-icons/md';
import { ModalOrganizationVideoDetails } from './ModalOrganizationVideoDetails';
import { useOrganizationVideoImportMutation } from 'lib/api/organizationVideo/useImportOrganizationVideoMutation';
import { useAuth } from 'lib/context';
import { useArchiveOrganizationVideoMutation } from 'lib/api/organizationVideo/useArchiveOrganizationVideoMutation';
import { useToastError } from 'lib/hooks';
import { ModalOrganizationVideoForm } from './ModalOrganizationVideoForm';
import { BsPlayFill } from 'react-icons/bs';
import { DEFAULT_PAGE, DEFAULT_SIZE } from './ModalOrganizationVideos';
import {
  Content,
  EmptySearch,
  Footer,
  VehicleLibrary,
  VehicleLibraryHeader,
  VehicleModel,
  VehicleModelHeader,
  VehiclePlaylist,
  VehiclePlaylistHeader,
  VehiclePreview,
  VehiclePreviewHeader,
  VehicleTitle,
  VehicleTitleHeader,
  VehicleYear,
  VehicleYearHeader,
  Video,
  VideoPlay,
  VideosList,
  VideoThumbnailWrapper,
} from './style';

type Props = {
  videos: OrganizationVideo[];
  count: number;
  isPreparingData: boolean;
  isFetchingVideos: boolean;
  isFetchingFilters: boolean;
  setSortKey: React.Dispatch<
    React.SetStateAction<OrganizationVideoSortKey | null>
  >;
  sortKey: OrganizationVideoSortKey | null;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  searchTerm: string;
  setOrder: React.Dispatch<
    React.SetStateAction<
      PaginationConstants.ASCENDING | PaginationConstants.DESCENDING
    >
  >;
  order: PaginationConstants.ASCENDING | PaginationConstants.DESCENDING;
  filters: OrganizationVideoFilter[];
  showAddToCovideo?: boolean;
  handleModalClose?: () => void;
  onCopy?: () => void;
  onSelectVideo?: (organizationVideo: OrganizationVideo) => void;
  filter: {
    [key: string]: string[];
  };
  handleFilterChange: (key: string, selectedOption: string[]) => void;
};

export const OrganizationVideosTable = ({
  videos,
  count,
  isPreparingData,
  isFetchingVideos,
  isFetchingFilters,
  setSortKey,
  sortKey,
  setPage,
  page,
  setSearchTerm,
  searchTerm,
  setOrder,
  order,
  filters,
  handleModalClose,
  onCopy,
  showAddToCovideo = true,
  onSelectVideo,
  filter,
  handleFilterChange,
}: Props) => {
  const {
    userData: { userOrganizationAccess },
  } = useAuth();
  const { showError } = useToastError();
  const [videoSource, setVideoSource] = useState('');
  const [showVideoDetails, setShowVideoDetails] =
    useState<OrganizationVideo | null>(null);
  const [showDeleteModal, setShowDeleteModal] =
    useState<OrganizationVideo | null>(null);
  const [showEditModal, setShowEditModal] = useState<OrganizationVideo | null>(
    null
  );
  const [showOrganizationVideoFormModal, setShowOrganizationVideoFormModal] =
    useState<OrganizationVideo | null>(null);

  const resetPage = () => {
    setPage(DEFAULT_PAGE);
  };

  const onSuccesCallback = () => {
    onCopy?.();
    handleModalClose?.();
  };

  // mutations
  const { isLoading: isImportingVideo, mutateAsync: importOrganizationVideo } =
    useOrganizationVideoImportMutation({
      onSuccesCallback,
    });

  const { mutateAsync: archiveOrganizationVideo, isLoading: isArchivingVideo } =
    useArchiveOrganizationVideoMutation();

  const isLoading = isPreparingData || isImportingVideo;

  const onSearchChangeHandler = async (search: string = '') => {
    setSearchTerm(search);
    resetPage();
  };

  const onPaginationChangeHandler = debounce(
    async ({ page: newPage }: { page: number }) => {
      if (page !== newPage) {
        setPage(newPage);
      }
    },
    300
  );

  const copyToCovideoHandler = (video: OrganizationVideo) => {
    importOrganizationVideo({
      organizationVideoId: video.organizationVideoId,
    });
  };

  const showSelectVideo = !!onSelectVideo;

  const handleSortChange = (
    sortKey: OrganizationVideoSortKey,
    order: PaginationConstants
  ) => {
    setOrder(order);
    setSortKey(sortKey);
    setPage(DEFAULT_PAGE);
  };

  const getEmptyStateMessage = () => {
    const hasFilters = Object.values(filter).some(
      val => Array.isArray(val) && val.length > 0
    );

    if (searchTerm && hasFilters) {
      return {
        primary: `No videos found matching "${searchTerm}" with your selected filters.`,
        secondary:
          'Try adjusting your filters or using a different search term.',
      };
    }
    if (searchTerm) {
      return {
        primary: `There are no videos with "${searchTerm}" in the title.`,
        secondary: 'Try using a different search term.',
      };
    }
    if (hasFilters) {
      return {
        primary: 'No videos match your selected filters.',
        secondary: 'Try selecting different filter options.',
      };
    }
    return {
      primary:
        "Unfortunately, there aren't any videos available at the moment.",
      secondary: '',
    };
  };

  const checkEditVideoPermission = (video: OrganizationVideo) => {
    if (showSelectVideo) {
      return false; // hide crud on merge modal
    }
    return !!(
      !!userOrganizationAccess &&
      userOrganizationAccess.length &&
      userOrganizationAccess.some(
        access => access.organizationId === video.organizationId.toString()
      )
    );
  };

  const handleSubmitArchiveVideo = async (video: OrganizationVideo) => {
    try {
      await archiveOrganizationVideo(video.organizationVideoId);
      setShowDeleteModal(null);
    } catch (error) {
      showError(error);
    }
  };

  const isFetchingData = isFetchingVideos || isFetchingFilters;

  if (isPreparingData) {
    return <LoadingIndicator isLoading={isPreparingData} />;
  }

  return (
    <Content>
      <Gap
        m='0 0 20px auto'
        alignItems='flex-end'
        justifyContent='space-between'
        width='100%'
      >
        <Gap gap='12px'>
          {filters?.map(item => {
            return (
              <MultiselectDropdown
                key={item.key}
                onOptionsSelected={value =>
                  handleFilterChange(item.key, value as string[])
                }
                options={item.options}
                menuListValue={-1}
                menuListLabel={`All ${item.name === 'Library' ? 'Libraries' : `${item.name}s`}`}
                name={`${item.name}`}
                menuShouldBlockScroll={false}
                isLoading={isFetchingData}
                selectInputWidth='200px'
                menuWidth='280px'
                isDisabled={isFetchingData}
                isSearchable
                isClearable
              />
            );
          })}
        </Gap>

        <Search
          placeholder='Search videos...'
          onSearch={onSearchChangeHandler}
          width='300px'
          disabled={isImportingVideo || isFetchingData}
        />
      </Gap>

      {isFetchingData ? (
        <div>
          <LoadingIndicator isLoading={isFetchingData} />
        </div>
      ) : (
        <>
          {!videos?.length ? (
            <EmptySearch>
              {(() => {
                const message = getEmptyStateMessage();
                return (
                  <>
                    <ParagraphSmallBold color={theme.palette.blue60}>
                      {message.primary}
                    </ParagraphSmallBold>
                    {message.secondary && (
                      <ParagraphSmallBold color={theme.palette.blue60}>
                        {message.secondary}
                      </ParagraphSmallBold>
                    )}
                  </>
                );
              })()}
            </EmptySearch>
          ) : (
            <>
              <VideosList>
                <Video>
                  <VehiclePreviewHeader>Preview</VehiclePreviewHeader>
                  <VehicleLibraryHeader>
                    Library
                    <SortButtons
                      isSorted={sortKey === OrganizationVideoSortKey.LIBRARY}
                      order={order}
                      onChange={value =>
                        handleSortChange(
                          OrganizationVideoSortKey.LIBRARY,
                          value
                        )
                      }
                    />
                  </VehicleLibraryHeader>
                  <VehiclePlaylistHeader>
                    Playlist
                    <SortButtons
                      isSorted={sortKey === OrganizationVideoSortKey.PLAYLIST}
                      order={order}
                      onChange={value =>
                        handleSortChange(
                          OrganizationVideoSortKey.PLAYLIST,
                          value
                        )
                      }
                    />
                  </VehiclePlaylistHeader>
                  <VehicleModelHeader>
                    Model
                    <SortButtons
                      isSorted={sortKey === OrganizationVideoSortKey.MODEL}
                      order={order}
                      onChange={value =>
                        handleSortChange(OrganizationVideoSortKey.MODEL, value)
                      }
                    />
                  </VehicleModelHeader>
                  <VehicleYearHeader>
                    Year
                    <SortButtons
                      isSorted={sortKey === OrganizationVideoSortKey.YEAR}
                      order={order}
                      onChange={value =>
                        handleSortChange(OrganizationVideoSortKey.YEAR, value)
                      }
                    />
                  </VehicleYearHeader>
                  <VehicleTitleHeader>
                    Title
                    <SortButtons
                      isSorted={sortKey === OrganizationVideoSortKey.TITLE}
                      order={order}
                      onChange={value =>
                        handleSortChange(OrganizationVideoSortKey.TITLE, value)
                      }
                    />
                  </VehicleTitleHeader>
                </Video>
                {videos?.map(video => (
                  <Video key={video.organizationVideoId}>
                    <VehiclePreview>
                      {video.thumbnailUrl ? (
                        <VideoItemThumbnail
                          handleClick={() => {
                            if (!!video.videoUrl) {
                              setVideoSource(video.videoUrl);
                            }
                          }}
                          thumbnail={video.thumbnailUrl}
                          videoId={''}
                          processing={0}
                          thumbnailWidth={'60px'}
                          thumbnailHeight={'40px'}
                        />
                      ) : (
                        <VideoThumbnailWrapper
                          onClick={() => {
                            if (!!video.videoUrl) {
                              setVideoSource(video.videoUrl);
                            }
                          }}
                        >
                          <video
                            src={video.videoUrl}
                            width={60}
                            height={40}
                            controls={false}
                          />

                          <VideoPlay>
                            <BsPlayFill color={theme.palette.white} size={30} />
                          </VideoPlay>
                        </VideoThumbnailWrapper>
                      )}
                    </VehiclePreview>
                    <VehicleLibrary title={video.library}>
                      {video.library}
                    </VehicleLibrary>
                    <VehiclePlaylist title={video.playlist}>
                      {video.playlist}
                    </VehiclePlaylist>
                    <VehicleModel title={video.model}>
                      {video.model}
                    </VehicleModel>
                    <VehicleYear>{video.year}</VehicleYear>
                    <VehicleTitle title={video.title}>
                      {video.title}
                    </VehicleTitle>
                    <Gap flexWrap='noWrap' gap='10px'>
                      <>
                        {checkEditVideoPermission(video) && (
                          <>
                            <Button
                              variant='destructive'
                              icon={<MdDeleteForever />}
                              onClick={() => setShowDeleteModal(video)}
                              title={'Delete Video'}
                            />
                            <Button
                              variant='secondary'
                              icon={<MdEdit />}
                              onClick={() => setShowEditModal(video)}
                              title={'Edit Video'}
                            />
                            <Button
                              variant='secondary'
                              icon={<MdContentCopy />}
                              onClick={() =>
                                setShowOrganizationVideoFormModal(video)
                              }
                              title={'Duplicate Video'}
                            />
                          </>
                        )}
                        {!!video.description && (
                          <Button
                            variant='secondary'
                            icon={<MdShortText size={16} />}
                            onClick={() => setShowVideoDetails(video)}
                            title={'Show Video Description'}
                          />
                        )}
                        {showAddToCovideo && (
                          <Button
                            variant='primary'
                            icon={<MdAdd />}
                            onClick={() => copyToCovideoHandler(video)}
                            title={'Add to Covideo'}
                          />
                        )}
                        {showSelectVideo && (
                          <Button
                            variant='secondary'
                            text='Select Video'
                            onClick={() => onSelectVideo(video)}
                          />
                        )}
                      </>
                    </Gap>
                  </Video>
                ))}
              </VideosList>
              <Footer>
                <TableContextProvider
                  total={count || 0}
                  initSize={DEFAULT_SIZE}
                  onChange={onPaginationChangeHandler}
                  initPage={page}
                >
                  <TablePaginationNew disabled={isLoading} />
                </TableContextProvider>
              </Footer>
            </>
          )}
        </>
      )}

      {!!showOrganizationVideoFormModal && (
        <ModalOrganizationVideoForm
          prefillData={{
            ...showOrganizationVideoFormModal,
            title: `Copy of ${showOrganizationVideoFormModal.title}`,
          }}
          handleModalClose={() => setShowOrganizationVideoFormModal(null)}
        />
      )}

      {!!videoSource && (
        <ModalVideoListVideo
          videoId={''}
          source={videoSource}
          handleModalClose={() => {
            setVideoSource('');
          }}
          hideContext={true}
        />
      )}

      {!!showVideoDetails && (
        <ModalOrganizationVideoDetails
          organizationVideo={showVideoDetails}
          handleModalClose={() => {
            setShowVideoDetails(null);
          }}
        />
      )}

      {!!showEditModal && (
        <ModalOrganizationVideoForm
          organizationVideo={showEditModal}
          handleModalClose={() => {
            setShowEditModal(null);
          }}
        />
      )}

      {showDeleteModal && (
        <ModalDelete
          title={`Delete Video`}
          orangeButtonText={'Keep it'}
          whiteButtonText={'Delete it'}
          text={`You are about to delete video ${showDeleteModal.title ? `"${showDeleteModal.title}"` : ''}`}
          hasConfirm={false}
          onClickOrangeButton={() => setShowDeleteModal(null)}
          onClickWhiteButton={() => handleSubmitArchiveVideo(showDeleteModal)}
          handleModalClose={() => setShowDeleteModal(null)}
          loading={isArchivingVideo}
        />
      )}
    </Content>
  );
};
